import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft, ChevronRight, Save, Send, Eye, Check, AlertCircle, Info,
  Code, LayoutTemplate, Users, Loader2, Download, Upload, ExternalLink,
} from 'lucide-react';
import {
  getCampaign, getTemplate, createCampaign, updateCampaign, submitCampaignForApproval,
  listSegments, listSenders, listTemplates, listTemplatesMitInhalt, templateHtml, fetchMyPermissions,
  edgeFunctions, isSegmentUsable,
  type Campaign, type DesignMode, type ManualRecipient, type Segment, type Template,
} from '../../lib/supabase';
import { HAUS_VORLAGEN, hausVorlage, kategorieText } from '../../lib/mailBuilder/htmlVorlagen';
import type { EmojiStufe } from '../../lib/mailBuilder/vorlagen';
import { offenePlatzhalter } from '../../lib/mailBuilder/platzhalter';
import { formatDateTime, roleLabel } from '../../lib/format';
import { istRedaktion, istTeam } from '../../lib/rollen';
import { brevoKampagneUrl } from '../../lib/brevo';
import { useAuthStore } from '../../stores/authStore';
import { StatusBadge } from '../../components/StatusBadge';
import { InlineTemplateEditor } from '../../components/InlineTemplateEditor';
import { PreSendChecklist } from '../../components/PreSendChecklist';
import { ManualRecipientPicker } from '../../components/ManualRecipientPicker';
import { MailMiniatur } from '../../components/MailVorschau/MailMiniatur';
import { EmojiStufenWahl } from '../../components/EmojiStufenWahl';
import './CampaignEditor.css';

type Step = 0 | 1 | 2 | 3 | 4;

const STEP_LABELS = [
  { key: 'basic',      label: 'Basis-Infos' },
  { key: 'recipients', label: 'Empfänger' },
  { key: 'design',     label: 'Design' },
  { key: 'preview',    label: 'Preview & Test' },
  { key: 'send',       label: 'Versand' },
];

const DEFAULT_SENDER_EMAIL = import.meta.env.VITE_DEFAULT_SENDER_EMAIL || 'no-reply@randori-pro.de';
const DEFAULT_SENDER_NAME  = import.meta.env.VITE_DEFAULT_SENDER_NAME  || 'Randori-Pro';

type FormState = Partial<Campaign> & { editor_design?: Record<string, unknown> | null };

// Status, in denen die Kampagne in Brevo noch nicht verschickt ist — nur dann
// wird der Brevo-Entwurf beim Speichern mitgezogen.
const BREVO_OPEN_STATUSES: Campaign['status'][] = ['draft', 'pending_approval', 'scheduled', 'failed'];

export function CampaignEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const canEdit = user?.role === 'admin' || user?.role === 'redakteur';

  const isNew = !id || id === 'new';

  const [step, setStep] = useState<Step>(0);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [testEmails, setTestEmails] = useState('');
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [postSendInfo, setPostSendInfo] = useState<{ kind: 'send' | 'approval'; recipientCount?: number } | null>(null);
  const [brevoBusy, setBrevoBusy] = useState(false);
  const [brevoNotice, setBrevoNotice] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>({
    name: '', subject: '', preview_text: '',
    sender_name: DEFAULT_SENDER_NAME,
    sender_email: DEFAULT_SENDER_EMAIL,
    reply_to: '',
    segment_ids: [], stripo_html: '', stripo_css: '',
    editor_design: null,
    design_mode: null,
    include_browser_view: true,
    include_unsubscribe_link: true,
    manual_recipients: [],
  });

  const campaignQuery = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => getCampaign(id!),
    enabled: !isNew,
    // Während des Versands läuft der Brevo-Teil im Hintergrund weiter — kurz pollen,
    // damit der Status hier von 'sending' automatisch auf 'sent'/'failed' springt.
    refetchInterval: (query) => {
      const data = query.state.data as Campaign | undefined;
      return data?.status === 'sending' ? 4000 : false;
    },
  });

  const segmentsQuery = useQuery({ queryKey: ['segments'], queryFn: listSegments });
  const sendersQuery  = useQuery({ queryKey: ['senders'],  queryFn: listSenders });
  const templatesQuery = useQuery({ queryKey: ['templates'], queryFn: listTemplates });
  const [vorlagenStufe, setVorlagenStufe] = useState<EmojiStufe>('normal');
  // Mit Inhalt nur, solange die Vorlagenwahl offen ist (für die Miniaturen).
  const vorlagenQuery = useQuery({
    queryKey: ['templates', 'inhalt'],
    queryFn: listTemplatesMitInhalt,
    enabled: form.design_mode === 'template' && !form.template_id,
  });
  const vorlagenListe: Template[] = (() => {
    const alle = vorlagenQuery.data ?? templatesQuery.data ?? [];
    const haus = HAUS_VORLAGEN.map((v) => alle.find((t) => t.haus === v.key)).filter((t): t is Template => !!t);
    return [...haus, ...alle.filter((t) => !hausVorlage(t.haus))];
  })();
  const vorlagenMitStufen = vorlagenListe.some((t) => !!t.varianten?.ohne && !!t.varianten?.viele);
  const permissionsQuery = useQuery({ queryKey: ['my-permissions'], queryFn: fetchMyPermissions });
  const canBypassApproval = user?.role === 'admin' || (permissionsQuery.data?.can_bypass_approval ?? false);

  // Form-Hydration: nur einmal pro Kampagnen-ID die Daten aus der Query in den
  // Form-State uebernehmen. So ueberschreiben spaetere Cache-Updates (z.B. nach
  // einem Save) keine laufenden Eingaben des Users mehr.
  const hydratedForId = useRef<string | null>(null);
  useEffect(() => {
    if (!campaignQuery.data || !id) return;
    if (hydratedForId.current === id) return;
    setForm(campaignQuery.data as FormState);
    hydratedForId.current = id;
  }, [campaignQuery.data, id]);

  const createMutation = useMutation({
    mutationFn: createCampaign,
    onSuccess: (c) => {
      // Cache direkt mit dem Ergebnis befuellen, damit nach dem navigate keine
      // zweite Roundtrip-Loading-Anzeige passiert.
      queryClient.setQueryData(['campaign', c.id], c);
      hydratedForId.current = c.id;
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      navigate(`/campaigns/${c.id}`, { replace: true });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Partial<Campaign>) => updateCampaign(id!, payload),
    onSuccess: (data, variables) => {
      // Cache-Merge statt Replace: nur die geaenderten Felder ueberschreiben,
      // damit HTML/Design lokal nicht durch ein leichtes Update-Payload weggemerged wird.
      if (id) {
        queryClient.setQueryData(['campaign', id], (old: Campaign | undefined) =>
          old ? { ...old, ...variables, ...data } : data
        );
      }
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const sendMutation = useMutation({
    mutationFn: (schedule_at?: string) => edgeFunctions.sendCampaign(id!, schedule_at),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setChecklistOpen(false);
      setPostSendInfo({ kind: 'send', recipientCount: res?.recipient_count });
    },
    onError: (e) => alert('Versand fehlgeschlagen: ' + (e as Error).message),
  });

  const submitApprovalMutation = useMutation({
    mutationFn: () => submitCampaignForApproval(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      setChecklistOpen(false);
      setPostSendInfo({ kind: 'approval' });
    },
    onError: (e) => alert('Einreichen fehlgeschlagen: ' + (e as Error).message),
  });

  const testMutation = useMutation({
    mutationFn: (emails: string[]) => edgeFunctions.sendTestEmail(id!, emails),
    // Der Testversand legt den Brevo-Entwurf an bzw. traegt den Stand dorthin —
    // oder meldet, dass der Entwurf in Brevo geaendert wurde.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['campaign', id] }),
  });

  /**
   * Brevo-Entwurf auf den Mailtool-Stand bringen. Die Kampagnen gehen meist
   * direkt aus Brevo raus; frueher blieb der Entwurf dort auf dem Stand des
   * ersten Testversands. Wurde er in Brevo geaendert, uebertraegt das Backend
   * nichts und die Kampagne zeigt den Hinweis mit den zwei Wegen.
   */
  const syncBrevoDraft = async (force = false) => {
    if (!id || isNew) return;
    setBrevoBusy(true);
    setBrevoNotice(null);
    try {
      const res = await edgeFunctions.brevoDraft(id, 'push', force);
      if (res.result === 'not_draft') {
        setBrevoNotice('Diese Kampagne ist in Brevo schon verschickt oder geplant — der Stand dort bleibt, wie er ist.');
      }
    } catch (e) {
      setBrevoNotice('Brevo-Entwurf nicht aktualisiert: ' + (e as Error).message);
    } finally {
      setBrevoBusy(false);
      await queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    }
  };

  const pullBrevoDraft = async () => {
    if (!id) return;
    if (!confirm('Der Stand aus Brevo (Text, Betreff, Vorschautext, Absender) ersetzt den Stand hier im Mailtool. Fortfahren?')) return;
    setBrevoBusy(true);
    setBrevoNotice(null);
    try {
      await edgeFunctions.brevoDraft(id, 'pull');
      // Formular mit dem uebernommenen Stand neu fuellen.
      hydratedForId.current = null;
    } catch (e) {
      setBrevoNotice('Übernehmen aus Brevo fehlgeschlagen: ' + (e as Error).message);
    } finally {
      setBrevoBusy(false);
      await queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    }
  };

  const forcePushBrevoDraft = async () => {
    if (!confirm('Die Änderungen, die in Brevo gemacht wurden, gehen dabei verloren. Mailtool-Stand trotzdem nach Brevo übertragen?')) return;
    await syncBrevoDraft(true);
  };

  /**
   * Speichert den aktuellen Stand. Step-aware: nur die Felder des aktuellen
   * Schrittes gehen ins Update-Payload — spart bei step 0/1 das 50KB+ HTML.
   * Bei Step 2 wird stripo_html in html_content gespiegelt (Versand-Quelle).
   */
  const handleSave = async (): Promise<boolean> => {
    if (readOnly) return false;
    setSaving(true);
    try {
      if (isNew) {
        // Neue Kampagne: alles auf einmal anlegen (es gibt noch nichts in der DB).
        const payload: FormState = { ...form };
        if (step === 2) {
          payload.html_content = form.stripo_html ?? form.html_content ?? '';
          payload.editor_design = null;
        }
        await createMutation.mutateAsync(payload);
      } else {
        // Update: nur die Felder des aktuellen Steps senden — kein HTML bei step 0/1.
        const payload: Partial<Campaign> = {};
        if (step === 0) {
          payload.name = form.name;
          payload.subject = form.subject;
          payload.preview_text = form.preview_text;
          payload.sender_name = form.sender_name;
          payload.sender_email = form.sender_email;
          payload.reply_to = form.reply_to;
        } else if (step === 1) {
          payload.segment_ids = form.segment_ids;
          payload.manual_recipients = form.manual_recipients;
        } else if (step === 2) {
          payload.design_mode = form.design_mode;
          payload.template_id = form.template_id;
          payload.stripo_html = form.stripo_html;
          payload.html_content = form.stripo_html ?? form.html_content ?? '';
          payload.editor_design = null;
          payload.include_browser_view = form.include_browser_view;
          payload.include_unsubscribe_link = form.include_unsubscribe_link;
        }
        // Step 3+4: nichts persistent zu speichern (Preview/Test, Versand)
        if (Object.keys(payload).length > 0) {
          await updateMutation.mutateAsync(payload);
          // Basis-Infos oder Inhalt geaendert: den Brevo-Entwurf mitziehen,
          // sofern es schon einen gibt und er noch nicht verschickt ist.
          if ((step === 0 || step === 2) && current?.brevo_campaign_id && BREVO_OPEN_STATUSES.includes(current.status)) {
            await syncBrevoDraft();
          }
        }
      }
      setLastSavedAt(new Date());
      return true;
    } catch (err) {
      alert('Speichern fehlgeschlagen: ' + (err as Error).message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const validManualRecipients = (form.manual_recipients ?? []).filter(r => r.has_consent_confirmed).length;
  // Abgelaufene Gruppen blocken den Versand komplett — User muss sie abwaehlen
  // oder eine neue Gruppe anlegen.
  const selectedSegments = (segmentsQuery.data ?? []).filter(s => form.segment_ids?.includes(s.id));
  const alwaysIncludedExtra = (segmentsQuery.data ?? []).filter(s => s.always_included && !form.segment_ids?.includes(s.id)).length;
  const recipientGroupCount = (form.segment_ids?.length ?? 0) + alwaysIncludedExtra;
  const hasExpiredSelected = selectedSegments.some(s => !isSegmentUsable(s));
  const canSend = !!(form.name && form.subject && form.sender_email
    && ((form.segment_ids?.length ?? 0) > 0 || validManualRecipients > 0)
    && !hasExpiredSelected);

  if (!isNew && campaignQuery.isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>;
  }

  const current = campaignQuery.data;
  // Mails aus dem Assistenten bearbeitet man im Assistenten (dort stehen Vorab-
  // Fragen, Empfänger, Testmail und Versand-Check); die Redaktion prüft sie unter
  // „Freigaben". Hier nur ansehen — sonst würde alles doppelt abgefragt, und bei
  // Mails von Geschäftsführung und Studioleitung liefe etwas an der Freigabe vorbei.
  const ausAssistent = !!current?.builder_template || !!current?.requires_approval;
  const readOnly = !canEdit || ausAssistent || (current && (current.status === 'sent' || current.status === 'sending'));

  const selectDesignMode = (mode: DesignMode) => {
    setForm({ ...form, design_mode: mode });
  };

  /**
   * Template auswählen: Vollständige Vorlage laden (HTML inklusive) und in den Inline-Editor übernehmen.
   * Die Liste enthält keine HTML-Inhalte (Performance), darum hier per ID nachladen.
   */
  const selectTemplate = async (templateId: string) => {
    try {
      const t = await getTemplate(templateId);
      if (!t) return;
      // Haus-Vorlagen in der gewählten Emoji-Stufe (ohne · normal · viele)
      const html = templateHtml(t, vorlagenStufe);
      setForm({
        ...form,
        template_id: t.id,
        stripo_html: html,
        html_content: html,
        editor_design: null,
        design_mode: 'template',
      });
    } catch (e) {
      alert('Vorlage konnte nicht geladen werden: ' + (e as Error).message);
    }
  };

  const savedIndicator = () => {
    if (saving) return <span style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>Speichert...</span>;
    if (lastSavedAt) {
      const sec = Math.round((Date.now() - lastSavedAt.getTime()) / 1000);
      return (
        <span style={{ fontSize: '0.8125rem', color: 'var(--success-dark)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Check size={13} /> Gespeichert {sec < 5 ? 'eben' : `vor ${sec}s`}
        </span>
      );
    }
    return null;
  };

  return (
    <div className="campaign-editor">
      <div className="editor-topbar">
        <button className="btn btn-ghost" onClick={() => navigate('/campaigns')}>
          <ChevronLeft size={16} /> Zurück
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '1.375rem', fontWeight: 700, margin: 0 }}>
            {isNew ? 'Neue Kampagne' : form.name || current?.name}
          </h1>
          <div style={{ marginTop: '0.25rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {current && <StatusBadge status={current.status} />}
            {savedIndicator()}
            {current?.brevo_campaign_id && (
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                {brevoBusy
                  ? <><Loader2 size={13} className="spin" aria-hidden="true" /> Brevo-Entwurf wird aktualisiert …</>
                  : current.brevo_sync_state === 'conflict'
                    ? <>Brevo-Entwurf #{current.brevo_campaign_id}: in Brevo geändert</>
                    : current.brevo_synced_at
                      ? <>Brevo-Entwurf #{current.brevo_campaign_id} · Stand vom {formatDateTime(current.brevo_synced_at)}</>
                      : <>Brevo-Entwurf #{current.brevo_campaign_id}</>}
              </span>
            )}
            {current?.brevo_campaign_id && istTeam(user) && (
              <a
                href={brevoKampagneUrl(current.brevo_campaign_id, current.status) ?? undefined}
                target="_blank" rel="noopener noreferrer"
                style={{ fontSize: '0.8125rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                title="Öffnet diese Kampagne direkt in Brevo (neuer Tab) — falls der Abgleich einmal nicht stimmt"
              >
                <ExternalLink size={13} aria-hidden="true" /> In Brevo öffnen
              </a>
            )}
          </div>
        </div>
        <button className="btn btn-secondary" onClick={() => handleSave()} disabled={saving || !!readOnly}>
          {saving ? <div className="loading-spinner" /> : <Save size={16} />}
          Speichern
        </button>
      </div>

      {ausAssistent && current && (
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          <Info size={16} />
          <div style={{ flex: 1 }}>
            <strong>Mail aus dem Assistenten</strong> von {current.created_by_name ?? 'unbekannt'}{current.created_by_role ? ` (${roleLabel(current.created_by_role)})` : ''}.
            {' '}Bearbeitet, getestet und verschickt oder eingereicht wird sie im Assistenten; die Redaktion prüft sie unter „Freigaben".
            Hier ist sie nur zum Ansehen — mit Statistik nach dem Versand.
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
              {current.builder_template && current.status === 'draft' && (current.created_by === user?.id || user?.role === 'admin') && (
                <Link to={`/neue-mail/${current.id}`} className="btn btn-primary btn-sm">Im Assistenten öffnen</Link>
              )}
              {istRedaktion(user) && current.builder_template && current.status === 'pending_approval' && current.created_by !== user?.id && (
                <Link to={`/neue-mail/${current.id}`} className="btn btn-secondary btn-sm">Im Assistenten korrigieren</Link>
              )}
              {istRedaktion(user) && (current.status === 'pending_approval' || current.review_state) && (
                <Link to={`/freigaben?mail=${current.id}`} className="btn btn-ghost btn-sm">Zu den Freigaben</Link>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="step-indicator">
        {STEP_LABELS.map((s, idx) => (
          <button
            key={s.key}
            className={`step-item${idx === step ? ' active' : ''}${idx < step ? ' done' : ''}`}
            onClick={async () => {
              // Wenn man zu einem späteren Schritt springen will, vorher speichern
              if (idx > step) await handleSave();
              setStep(idx as Step);
            }}
          >
            <div className="step-dot">{idx < step ? <Check size={14} /> : idx + 1}</div>
            <span>{s.label}</span>
          </button>
        ))}
      </div>

      {current?.brevo_sync_state === 'conflict' && current.brevo_campaign_id && BREVO_OPEN_STATUSES.includes(current.status) && !readOnly && (
        <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
          <AlertCircle size={16} />
          <div style={{ flex: 1 }}>
            <strong>Der Entwurf wurde in Brevo geändert.</strong> Seit das Mailtool ihn zuletzt übertragen hat,
            wurden in Brevo Text, Betreff oder Absender geändert. Damit das nicht verloren geht, überträgt das
            Mailtool nichts mehr und verschickt keine Testmail, bis feststeht, welcher Stand gilt.
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary btn-sm" onClick={pullBrevoDraft} disabled={brevoBusy}>
                <Download size={14} /> Brevo-Stand ins Mailtool übernehmen
              </button>
              <button className="btn btn-ghost btn-sm" onClick={forcePushBrevoDraft} disabled={brevoBusy}>
                <Upload size={14} /> Mailtool-Stand nach Brevo übertragen
              </button>
            </div>
          </div>
        </div>
      )}
      {(brevoNotice || (current?.brevo_sync_state === 'error' && current.brevo_sync_error)) && (
        <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
          <AlertCircle size={16} />
          <div>{brevoNotice ?? `Brevo-Entwurf nicht aktualisiert: ${current?.brevo_sync_error}`}</div>
        </div>
      )}

      <div className="card">
        {step === 0 && (
          <div>
            <h2 className="card-title" style={{ marginBottom: '1.25rem' }}>Basis-Informationen</h2>
            <div className="form-group">
              <label className="form-label">Interner Name *</label>
              <input type="text" className="form-input" placeholder="z.B. Fight Night Mai 2026"
                value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={!!readOnly} />
              <p className="form-hint">Wird nur intern angezeigt, nicht an Empfänger</p>
            </div>
            <div className="form-group">
              <label className="form-label">Betreff *</label>
              <input type="text" className="form-input" placeholder="z.B. Fight Night am 15. Mai – jetzt anmelden"
                value={form.subject ?? ''} onChange={(e) => setForm({ ...form, subject: e.target.value })} disabled={!!readOnly} />
            </div>
            <div className="form-group">
              <label className="form-label">Preview-Text</label>
              <input type="text" className="form-input" placeholder="Kurzer Text, der in der Inbox nach dem Betreff erscheint"
                value={form.preview_text ?? ''} onChange={(e) => setForm({ ...form, preview_text: e.target.value })} disabled={!!readOnly} />
              <p className="form-hint">Max. 80-100 Zeichen empfohlen</p>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Absender-Adresse *</label>
                <select className="form-select" value={form.sender_email ?? ''}
                  onChange={(e) => {
                    const sender = sendersQuery.data?.find(s => s.email === e.target.value);
                    setForm({ ...form, sender_email: e.target.value, sender_name: sender?.name ?? form.sender_name });
                  }} disabled={!!readOnly}>
                  <option value="">-- wählen --</option>
                  {(sendersQuery.data ?? []).map(s => (
                    <option key={s.id} value={s.email}>
                      {s.name} ({s.email}){!s.is_verified && ' ⚠ nicht verifiziert'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Absender-Name *</label>
                <input type="text" className="form-input" placeholder="z.B. Randori-Pro Newsletter"
                  value={form.sender_name ?? ''} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} disabled={!!readOnly} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Reply-To</label>
              <input type="email" className="form-input" placeholder="Optional – wohin sollen Antworten gehen?"
                value={form.reply_to ?? ''} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} disabled={!!readOnly} />
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h2 className="card-title" style={{ marginBottom: '1.25rem' }}>Empfänger wählen</h2>
            <div className="alert alert-info" style={{ marginBottom: '1.5rem' }}>
              <AlertCircle size={16} />
              <div>
                Es werden ausschließlich Kontakte mit <strong>aktivem Marketing-Opt-In</strong> und
                ohne Abmeldung versendet. Hard-Bounces werden ebenfalls ausgeschlossen.
              </div>
            </div>
            <div className="alert alert-info" style={{ marginBottom: '1.5rem' }}>
              <AlertCircle size={16} />
              <div>
                <strong>Immer in Kopie:</strong> Studios und Backoffice, alle Studioleitungen, die
                Mail-Redaktion und die Geschäftsführung bekommen jede Mail automatisch — die
                Gruppen musst du nicht anhaken. Das gilt auch für den Entwurf, den das Mailtool
                beim Testversand in Brevo anlegt: Wer direkt aus Brevo verschickt, wählt dort nur
                noch die Zielgruppe dazu.
              </div>
            </div>
            {(() => {
              const segments = segmentsQuery.data ?? [];
              const favorites = segments.filter(s => s.is_favorite);
              const others    = segments.filter(s => !s.is_favorite);
              const renderSegmentItem = (s: Segment) => {
                const usable = isSegmentUsable(s);
                // Interne Kopie-Verteiler haengt der Versand selbst an — hier
                // nur sichtbar machen, nicht abwaehlbar.
                const always = s.always_included;
                const checked = always || (form.segment_ids?.includes(s.id) ?? false);
                const expiredAndSelected = !usable && checked;
                return (
                  <label key={s.id} className="segment-picker-item"
                    style={{ opacity: !usable ? 0.6 : 1, position: 'relative' }}>
                    <input type="checkbox" checked={checked}
                      onChange={(e) => {
                        const ids = form.segment_ids ?? [];
                        setForm({ ...form, segment_ids: e.target.checked ? [...ids, s.id] : ids.filter(x => x !== s.id) });
                      }}
                      disabled={!!readOnly || always || (!usable && !checked)} />
                    <div>
                      <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                        {s.name}
                        {always && <span className="status-badge" style={{ background: 'var(--info-light)', color: 'var(--info-dark)', fontSize: '0.65rem' }}>immer dabei</span>}
                        {s.admin_only && <span className="status-badge" style={{ background: 'var(--danger-light)', color: 'var(--danger-dark)', fontSize: '0.65rem' }}>Admin-only</span>}
                        {!usable && <span className="status-badge" style={{ background: 'var(--danger-light)', color: 'var(--danger-dark)', fontSize: '0.65rem' }}>abgelaufen</span>}
                      </div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
                        {s.contact_count} Kontakte · {s.is_dynamic ? 'dynamisch' : 'statisch'}
                        {s.valid_until && <> · gültig bis {new Date(s.valid_until).toLocaleDateString('de-DE')}</>}
                      </div>
                      {expiredAndSelected && (
                        <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--danger-dark)' }}>
                          ⚠ Diese Gruppe ist abgelaufen — bitte abwählen oder neu anlegen.
                        </div>
                      )}
                    </div>
                  </label>
                );
              };
              return (
                <div className="form-group">
                  {segments.length === 0 ? (
                    <p style={{ color: 'var(--text-tertiary)' }}>
                      Noch keine Gruppen angelegt — <a href="/segments/new" style={{ color: 'var(--primary)' }}>erst eine Gruppe erstellen</a>.
                    </p>
                  ) : (
                    <>
                      {favorites.length > 0 && (
                        <>
                          <label className="form-label">⭐ Favoriten</label>
                          <div className="segment-picker" style={{ marginBottom: '1rem' }}>
                            {favorites.map(renderSegmentItem)}
                          </div>
                        </>
                      )}
                      {others.length > 0 && (
                        <>
                          <label className="form-label">Weitere Gruppen</label>
                          <div className="segment-picker">
                            {others.map(renderSegmentItem)}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            <div className="form-group" style={{ marginTop: '1.5rem' }}>
              <label className="form-label">Einzelne Empfänger (zusätzlich)</label>
              <p className="form-hint" style={{ marginTop: 0 }}>
                Optional — füge einzelne Personen direkt hinzu, ohne dafür eine Gruppe anzulegen.
                Ideal für Test-Empfänger oder Ad-hoc-Versand an einzelne Personen.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button className="btn btn-secondary" onClick={() => setPickerOpen(true)} disabled={!!readOnly}>
                  <Users size={14} /> Empfänger einzeln hinzufügen
                </button>
                {(form.manual_recipients?.length ?? 0) > 0 && (
                  <span className="status-badge" style={{ background: 'var(--info-light)', color: 'var(--info-dark)' }}>
                    {form.manual_recipients?.length} manuell hinzugefügt
                    {(() => {
                      const blocked = (form.manual_recipients ?? []).filter(r => !r.has_consent_confirmed).length;
                      return blocked > 0 ? <> · {blocked} ohne Einwilligung blockiert</> : null;
                    })()}
                  </span>
                )}
              </div>
              {(form.manual_recipients?.length ?? 0) > 0 && (
                <div style={{ marginTop: '0.75rem', maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', background: 'var(--surface-secondary)' }}>
                  {(form.manual_recipients ?? []).map((r, idx) => (
                    <div key={`${r.email}-${idx}`} style={{
                      padding: '0.4rem 0.75rem', fontSize: '0.8125rem',
                      borderBottom: idx < (form.manual_recipients?.length ?? 0) - 1 ? '1px solid var(--border-light)' : 'none',
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      color: !r.has_consent_confirmed ? 'var(--danger-dark)' : 'var(--text-primary)',
                    }}>
                      <span style={{ fontFamily: 'monospace', flex: 1 }}>{r.email}</span>
                      {[r.first_name, r.last_name].filter(Boolean).join(' ') && (
                        <span style={{ color: 'var(--text-tertiary)' }}>({[r.first_name, r.last_name].filter(Boolean).join(' ')})</span>
                      )}
                      {r.source === 'manual' && <span style={{ fontSize: '0.7rem', color: 'var(--warning-dark)' }}>frei eingetragen</span>}
                      {!r.has_consent_confirmed && <span style={{ fontSize: '0.7rem' }}>blockiert (kein Opt-In)</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="card-title" style={{ marginBottom: '0.25rem' }}>Design</h2>

            {!form.design_mode && (
              <>
                <p className="form-hint" style={{ marginBottom: '1.5rem' }}>Wähle, wie du dein Design erstellen möchtest:</p>
                <div className="mode-grid">
                  <button type="button" className="mode-card" onClick={() => selectDesignMode('template')} disabled={!!readOnly}>
                    <div className="mode-icon" style={{ background: 'var(--warning-light)', color: 'var(--warning-dark)' }}>
                      <LayoutTemplate size={28} />
                    </div>
                    <div className="mode-title">Vorlage auswählen</div>
                    <div className="mode-desc">Vorgefertigte Templates als Ausgangspunkt. Texte und Bilder kannst du direkt darin austauschen.</div>
                  </button>

                  <button type="button" className="mode-card" onClick={() => selectDesignMode('html')} disabled={!!readOnly}>
                    <div className="mode-icon" style={{ background: 'var(--info-light)', color: 'var(--info-dark)' }}>
                      <Code size={28} />
                    </div>
                    <div className="mode-title">Eigenes HTML einfügen</div>
                    <div className="mode-desc">Direkt rohes HTML einfügen — für eigene oder aus anderen Tools exportierte Designs.</div>
                  </button>
                </div>
              </>
            )}

            {form.design_mode && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0.5rem 0 1rem' }}>
                <p className="form-hint" style={{ margin: 0 }}>
                  Modus: <strong>{
                    form.design_mode === 'html'      ? 'Eigenes HTML' :
                    form.template_id                 ? 'Vorlage – Inline-Editor' :
                    'Vorlage auswählen'
                  }</strong>
                </p>
                <button className="btn btn-ghost btn-sm" onClick={() => setForm({ ...form, design_mode: null })}>
                  Modus wechseln
                </button>
              </div>
            )}

            {form.design_mode === 'html' && (
              <>
                <p className="form-hint" style={{ marginBottom: '1rem' }}>
                  Füge hier dein fertiges HTML ein. <strong>Abmelde-Link</strong> und <strong>Browser-Ansicht-Link</strong> werden je nach Auswahl unten automatisch eingefügt.
                </p>
                <textarea
                  className="form-textarea"
                  style={{ minHeight: '500px', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.8125rem' }}
                  placeholder={'<!DOCTYPE html>\n<html>\n  <head>\n    <meta charset="utf-8">\n  </head>\n  <body>\n    ...\n  </body>\n</html>'}
                  value={form.stripo_html ?? ''}
                  onChange={(e) => setForm({ ...form, stripo_html: e.target.value, html_content: e.target.value, editor_design: null })}
                  disabled={!!readOnly}
                />
              </>
            )}

            {form.design_mode === 'template' && !form.template_id && (
              <>
                <div className="vorlagen-kopf">
                  <p className="form-hint" style={{ margin: 0 }}>
                    Wähle eine Vorlage. Texte und Bilder kannst du direkt darin austauschen, ohne die Vorlage selbst zu verändern.
                    {vorlagenMitStufen && ' Die Haus-Vorlagen gibt es ohne, mit normal vielen oder mit vielen Emojis.'}
                  </p>
                  {vorlagenMitStufen && <EmojiStufenWahl id="kampagne-vorlagen-emojis" wert={vorlagenStufe} onChange={setVorlagenStufe} />}
                </div>
                {vorlagenListe.length === 0 ? (
                  <div className="empty-state">
                    <LayoutTemplate size={48} />
                    <h3>Noch keine Vorlagen angelegt</h3>
                    <p>
                      Admins legen sie im Menü unter <Link to="/templates" style={{ color: 'var(--primary)' }}>HTML-Vorlagen</Link> an.
                    </p>
                  </div>
                ) : (
                  <div className="template-gallery">
                    {vorlagenListe.map(t => {
                      const html = templateHtml(t, vorlagenStufe);
                      return (
                      <button key={t.id} type="button"
                        className={`template-card${form.template_id === t.id ? ' selected' : ''}`}
                        onClick={() => selectTemplate(t.id)}
                        disabled={!!readOnly}>
                        <div className="template-thumb">
                          {t.thumbnail_url && !hausVorlage(t.haus) ? (
                            <img src={t.thumbnail_url} alt={t.name} />
                          ) : html ? (
                            <MailMiniatur html={html} />
                          ) : (
                            <div className="template-thumb-placeholder"><LayoutTemplate size={36} /></div>
                          )}
                        </div>
                        <div className="template-info">
                          <div className="template-name">{t.name}</div>
                          {t.category && <span className="template-category">{kategorieText(t.category)}</span>}
                          {t.description ? (
                            <div className="template-desc">{t.description}</div>
                          ) : (
                            <div className="template-desc-empty">Keine Beschreibung hinterlegt.</div>
                          )}
                        </div>
                        {form.template_id === t.id && <div className="template-check"><Check size={14} /></div>}
                      </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {form.design_mode === 'template' && form.template_id && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <p className="form-hint" style={{ margin: 0 }}>
                    Vorlage: <strong>{templatesQuery.data?.find(t => t.id === form.template_id)?.name ?? '—'}</strong>
                    {' · '}Text und Bilder direkt in der Vorlage bearbeiten.
                  </p>
                  <button className="btn btn-ghost btn-sm" onClick={() => setForm({ ...form, template_id: null, stripo_html: '', html_content: '', editor_design: null })} disabled={!!readOnly}>
                    Andere Vorlage auswählen
                  </button>
                </div>
                <InlineTemplateEditor
                  html={form.stripo_html ?? form.html_content ?? ''}
                  onChange={(newHtml) => setForm({ ...form, stripo_html: newHtml, html_content: newHtml })}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => handleSave()} disabled={!!readOnly || saving}>
                    <Save size={14} /> Speichern
                  </button>
                </div>
              </>
            )}

            {form.design_mode && (
              <div className="card" style={{ marginTop: '1.5rem', background: 'var(--surface-secondary)' }}>
                <h3 style={{ marginTop: 0, fontSize: '0.95rem' }}>Footer-Optionen</h3>
                <p className="form-hint" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
                  Diese Hinweise kommen automatisch in die E-Mail – auch in den Brevo-Entwurf. Bringt dein HTML schon
                  einen eigenen Abmelde-Link mit, wird kein zweiter angehängt.
                </p>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '0.5rem' }}>
                  <input type="checkbox" checked={form.include_browser_view !== false}
                    onChange={(e) => setForm({ ...form, include_browser_view: e.target.checked })} disabled={!!readOnly} />
                  <span>
                    <strong>Im Browser ansehen</strong>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>Hinweis am Anfang der E-Mail mit Link zur Web-Ansicht (Brevo {'{{ mirror }}'}).</div>
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                  <input type="checkbox" checked={form.include_unsubscribe_link !== false}
                    onChange={(e) => setForm({ ...form, include_unsubscribe_link: e.target.checked })} disabled={!!readOnly} />
                  <span>
                    <strong>Abmelde-Link einfügen</strong>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>Pflicht laut DSGVO. Footer mit Abmelde-Link wird unten angehängt (Brevo {'{{ unsubscribe }}'}).</div>
                  </span>
                </label>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="card-title" style={{ marginBottom: '1.25rem' }}>Preview & Test-Versand</h2>
            <div className="preview-grid">
              <div>
                <label className="form-label">Preview</label>
                <div className="preview-frame">
                  <iframe srcDoc={form.stripo_html || form.html_content || '<p style="color:#888;padding:2rem">Noch kein Inhalt — erst im Design-Step speichern</p>'} title="Preview" sandbox="" />
                </div>
              </div>
              <div>
                <label className="form-label">Test-E-Mail senden</label>
                <p className="form-hint" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
                  Mehrere Adressen mit Komma trennen. Die Testmail kommt aus dem Brevo-Entwurf; vorher wird der
                  aktuelle Stand dorthin übertragen. Sie zeigt also genau das, was aus Brevo verschickt wird.
                </p>
                <input type="text" className="form-input" placeholder="dein@name.de, test@randori-pro.de"
                  value={testEmails} onChange={(e) => setTestEmails(e.target.value)} />
                <button className="btn btn-secondary" style={{ marginTop: '0.75rem', width: '100%' }}
                  disabled={!testEmails.trim() || !id || testMutation.isPending || isNew}
                  onClick={() => testMutation.mutate(testEmails.split(',').map(e => e.trim()).filter(Boolean))}>
                  <Eye size={16} />
                  {testMutation.isPending ? 'Sende...' : 'Test senden'}
                </button>
                {testMutation.isSuccess && (<div className="alert alert-success" style={{ marginTop: '1rem' }}><Check size={16} /><div>Test-E-Mail wurde versendet.</div></div>)}
                {testMutation.isError && (<div className="alert alert-danger" style={{ marginTop: '1rem' }}><AlertCircle size={16} /><div>Test-Versand fehlgeschlagen: {(testMutation.error as Error).message}</div></div>)}
                {isNew && (<p className="form-hint">Zuerst speichern, dann Test-E-Mail möglich.</p>)}
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 className="card-title" style={{ marginBottom: '1.25rem' }}>Versand</h2>
            {canBypassApproval ? (
              <div className="alert alert-warning">
                <AlertCircle size={16} />
                <div><strong>Achtung:</strong> Das Versenden kann nicht rückgängig gemacht werden. Bitte Inhalte sorgfältig prüfen und mindestens eine Test-E-Mail verschickt haben.</div>
              </div>
            ) : (
              <div className="alert alert-info">
                <Info size={16} />
                <div>
                  <strong>Freigabe-Workflow:</strong> Du kannst diese Kampagne nicht direkt versenden.
                  Reiche sie zur Freigabe ein – die Redaktion wird per E-Mail informiert und entscheidet
                  unter „Freigaben". Erst nach der Freigabe wird die Kampagne tatsächlich versendet.
                </div>
              </div>
            )}
            <div className="card" style={{ marginTop: '1.5rem', background: 'var(--surface-secondary)' }}>
              <h3 style={{ marginTop: 0 }}>Zusammenfassung</h3>
              <dl className="summary-list">
                <dt>Name</dt><dd>{form.name}</dd>
                <dt>Betreff</dt><dd>{form.original_subject ?? form.subject}</dd>
                <dt>Absender</dt><dd>{form.sender_name} &lt;{form.sender_email}&gt;</dd>
                <dt>Empfänger-Gruppen</dt><dd>{form.segment_ids?.length ?? 0} ausgewählt{alwaysIncludedExtra > 0 && <> + {alwaysIncludedExtra} immer in Kopie</>}</dd>
                <dt>Einzel-Empfänger</dt><dd>{validManualRecipients} (manuell hinzugefügt mit Einwilligung)</dd>
                <dt>Design-Modus</dt><dd>
                  {form.design_mode === 'html'      && <><Code size={12} style={{ display: 'inline', marginRight: 4 }} /> Eigenes HTML</>}
                  {form.design_mode === 'template'  && <><LayoutTemplate size={12} style={{ display: 'inline', marginRight: 4 }} /> Vorlage</>}
                  {!form.design_mode && <em>nicht gesetzt</em>}
                </dd>
                <dt>Browser-Ansicht</dt><dd>{form.include_browser_view !== false ? 'Ja' : 'Nein'}</dd>
                <dt>Abmelde-Link</dt><dd>{form.include_unsubscribe_link !== false ? 'Ja' : 'Nein'}</dd>
              </dl>
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              {canBypassApproval ? (
                <button className="btn btn-primary"
                  disabled={!canSend || sendMutation.isPending || !!readOnly || isNew}
                  onClick={() => setChecklistOpen(true)}>
                  {sendMutation.isPending ? <Loader2 size={16} className="spin" aria-hidden="true" /> : <Send size={16} />}
                  {sendMutation.isPending ? 'Wird an Brevo übergeben...' : 'Weiter zum finalen Check'}
                </button>
              ) : (
                <button className="btn btn-primary"
                  disabled={!canSend || submitApprovalMutation.isPending || !!readOnly || isNew || current?.status === 'pending_approval'}
                  onClick={() => setChecklistOpen(true)}>
                  {submitApprovalMutation.isPending ? <Loader2 size={16} className="spin" aria-hidden="true" /> : <Send size={16} />}
                  {submitApprovalMutation.isPending ? 'Wird eingereicht...' :
                   current?.status === 'pending_approval' ? 'Bereits eingereicht' :
                   'Weiter zum finalen Check'}
                </button>
              )}
            </div>
            {sendMutation.isError && (<div className="alert alert-danger" style={{ marginTop: '1rem' }}><AlertCircle size={16} /><div>Versand fehlgeschlagen: {(sendMutation.error as Error).message}</div></div>)}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-light)' }}>
          <button className="btn btn-ghost" disabled={step === 0} onClick={() => setStep((step - 1) as Step)}>
            <ChevronLeft size={16} /> Zurück
          </button>
          <button
            className="btn btn-primary"
            disabled={step === 4 || saving}
            onClick={async () => {
              // Immer speichern (auch bei neuen Kampagnen) — dann weiter
              const ok = await handleSave();
              if (ok) setStep((step + 1) as Step);
            }}
          >
            {saving ? 'Speichere...' : <>Speichern & Weiter <ChevronRight size={16} /></>}
          </button>
        </div>
      </div>

      <PreSendChecklist
        isOpen={checklistOpen}
        onClose={() => setChecklistOpen(false)}
        pending={sendMutation.isPending || submitApprovalMutation.isPending}
        mode={canBypassApproval ? 'send' : 'approval'}
        meta={{
          name: form.name ?? undefined,
          subject: form.original_subject ?? form.subject ?? undefined,
          senderName: form.sender_name ?? undefined,
          senderEmail: form.sender_email ?? undefined,
          recipientGroupCount,
          manualRecipientCount: validManualRecipients,
          hasUnsubscribe: form.include_unsubscribe_link !== false,
          hasBrowserView: form.include_browser_view !== false,
          designMode: form.design_mode,
          hasSentTestEmail: testMutation.isSuccess,
          placeholders: offenePlatzhalter(form.stripo_html || form.html_content, form.original_subject ?? form.subject, form.preview_text),
        }}
        onConfirm={() => {
          if (canBypassApproval) sendMutation.mutate(undefined);
          else                    submitApprovalMutation.mutate();
        }}
      />

      <ManualRecipientPicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        current={form.manual_recipients ?? []}
        onSave={(next: ManualRecipient[]) => {
          // Direkt persistieren, damit es nicht verloren geht
          setForm({ ...form, manual_recipients: next });
          if (!isNew) updateMutation.mutate({ manual_recipients: next });
        }}
      />

      {postSendInfo && (
        <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && navigate('/campaigns')}>
          <div className="modal modal-md" style={{ maxWidth: 520 }}>
            <div className="modal-content" style={{ textAlign: 'center', padding: '2rem 1.5rem' }}>
              <div style={{
                width: 64, height: 64, margin: '0 auto 1rem',
                borderRadius: '50%',
                background: 'var(--success-light)', color: 'var(--success-dark)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Check size={32} />
              </div>
              <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem' }}>
                {postSendInfo.kind === 'send' ? 'An Versand übergeben' : 'Zur Freigabe eingereicht'}
              </h2>
              <p style={{ margin: '0 0 1.25rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {postSendInfo.kind === 'send' ? (
                  <>
                    Die Kampagne wurde erfolgreich für den Versand vorgemerkt.
                    {postSendInfo.recipientCount !== undefined ? <> {postSendInfo.recipientCount} versandfähige Empfänger.</> : null}
                    {' '}Brevo verarbeitet und versendet die Mails in den nächsten Minuten — der Status in der Übersicht
                    wechselt automatisch von „Wird gesendet" auf „Versendet". Stats erscheinen kurz danach unter
                    „Kampagnen → Statistik".
                  </>
                ) : (
                  <>Die Redaktion hat eine Nachricht bekommen und entscheidet unter „Freigaben". Den Stand siehst Du unter „Meine Mails".</>
                )}
              </p>
              <button className="btn btn-primary" onClick={() => { setPostSendInfo(null); navigate('/campaigns'); }}>
                Verstanden — zurück zur Übersicht
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
